import {
  divWithStyle,
  flexRowStyle,
  headerPaddingStyle,
  nonPlaceTitleStyle,
  SiteUrlsContext,
  spaceHeaderContentStyle,
  UrlSearch,
  UrlSearchProvider,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import {
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  TabStyles,
} from '@tableau/widgets';
import { History } from 'history';
import * as React from 'react';
import { Route } from 'react-router';
import { defaultMemoize } from 'reselect';

import { Messages } from '../../../gen-src/Localize';
import { CatalogBrowserSelectorItem } from './CatalogBrowserSelectorItem';

const Header = divWithStyle('CatalogBrowserHeader', [
  flexRowStyle,
  headerPaddingStyle,
  spaceHeaderContentStyle,
]);

const Title = divWithStyle('CatalogBrowserTitle', nonPlaceTitleStyle);

const Selector = divWithStyle('CatalogBrowserSelector', {
  paddingLeft: TabStyles.Spacing.ExtraLargeSpace,
});

const GraphiQLLink = divWithStyle('CatalogBrowserGraphiQLLink', {
  flex: 1,
  textAlign: 'right',
});

export enum CatalogBrowserSubplace {
  Databases = 'Databases',
  Tables = 'Tables',
}

export type CatalogBrowserHeaderProps = {
  readonly databasesCount: number | null;
  readonly tablesCount: number | null;
  readonly selectedSubplace: CatalogBrowserSubplace;
};

export class CatalogBrowserHeader extends React.Component<
  CatalogBrowserHeaderProps
> {
  public render(): React.ReactNode {
    return (
      <Route>
        {({ history }) => (
          <SiteUrlsContext.Consumer>
            {(urls) => (
              <UrlSearchProvider>
                {(urlSearch) => (
                  <CatalogBrowserHeaderInternal
                    databasesCount={this.props.databasesCount}
                    tablesCount={this.props.tablesCount}
                    selectedSubplace={this.props.selectedSubplace}
                    history={history}
                    urls={urls}
                    urlSearch={urlSearch}
                  />
                )}
              </UrlSearchProvider>
            )}
          </SiteUrlsContext.Consumer>
        )}
      </Route>
    );
  }
}

export type CatalogBrowserHeaderInternalProps = CatalogBrowserHeaderProps & {
  readonly history: History;
  readonly urls: SiteUrls;
  readonly urlSearch: UrlSearch;
};

export class CatalogBrowserHeaderInternal extends React.Component<
  CatalogBrowserHeaderInternalProps
> {
  // tslint:disable-next-line: typedef
  private readonly memoizedCreateSubplaceList = defaultMemoize(
    createSubplaceList
  );

  private renderDropdown = () => {
    const items = this.memoizedCreateSubplaceList(
      this.props.databasesCount,
      this.props.tablesCount
    );
    return (
      <Selector>
        <SingleSelectDropdown
          type={DropdownButtonType.Line}
          label={''}
          list={
            <List items={items} selectedValue={this.props.selectedSubplace} />
          }
          value={this.props.selectedSubplace}
          onChange={this.onChangeSubspace}
          style={{ fontSize: 14 }}
        >
          {this.props.selectedSubplace === CatalogBrowserSubplace.Databases
            ? Messages.DatabasesAndFiles()
            : Messages.Tables()}
        </SingleSelectDropdown>
      </Selector>
    );
  };

  public render(): React.ReactNode {
    return (
      <Header>
        <Title>{Messages.ExternalAssets()}</Title>
        {this.renderDropdown()}
        <GraphiQLLink>
          <a href='/metadata/graphiql/'>{Messages.QueryMetadataGraphiQL()}</a>
        </GraphiQLLink>
      </Header>
    );
  }

  private readonly onChangeSubspace = (value: string) => {
    if (this.props.selectedSubplace !== value) {
      switch (value) {
        case CatalogBrowserSubplace.Databases:
          this.props.history.push(
            this.props.urls.catalogDatabasesPath() + this.props.urlSearch.value
          );
          break;
        case CatalogBrowserSubplace.Tables:
          this.props.history.push(
            this.props.urls.catalogTablesPath() + this.props.urlSearch.value
          );
          break;
        default:
          break;
      }
    }
  };
}

function createSubplaceList(
  databasesCount: number | null,
  tablesCount: number | null
): ListItem[] {
  return [
    {
      value: CatalogBrowserSubplace.Databases,
      content: (
        <CatalogBrowserSelectorItem
          subplaceName={Messages.DatabasesAndFiles()}
          count={databasesCount}
        />
      ),
    },
    {
      value: CatalogBrowserSubplace.Tables,
      content: (
        <CatalogBrowserSelectorItem
          subplaceName={Messages.Tables()}
          count={tablesCount}
        />
      ),
    },
  ];
}
